// src/utils/debug.ts

/**
 * Pequeñas utilidades de depuración para Admin y Público.
 * - dlog: log con prefijo y timestamp (respeta VITE_DEBUG).
 * - dtoast: toast minimalista sin dependencias (o usa window.toast si existe).
 */

type AnyFn = (...args: any[]) => void

// Habilitar/forzar logs: define VITE_DEBUG=true en .env si querés verlos en prod.
const isProd = typeof import.meta !== 'undefined'
  ? import.meta.env.MODE === 'production'
  : false

const FORCE_DEBUG = typeof import.meta !== 'undefined'
  ? (import.meta.env.VITE_DEBUG === 'true' || import.meta.env.VITE_DEBUG === '1')
  : false

const DEBUG_ENABLED = FORCE_DEBUG || !isProd

const prefix = (tag: string) => `[${tag}] ${new Date().toLocaleTimeString('es-AR')}`

/** Log con prefijo; agrupa si hay objetos */
export const dlog: AnyFn = (...args: any[]) => {
  if (!DEBUG_ENABLED) return
  try {
    if (args.length === 1 && typeof args[0] === 'string') {
      console.log(prefix('PUBLIC DEBUG'), args[0])
      return
    }
    // Si hay varios argumentos/objetos, mostramos en grupo
    console.groupCollapsed(prefix('PUBLIC DEBUG'))
    for (const a of args) console.log(a)
    console.groupEnd()
  } catch {
    // fallback silencioso
  }
}

/** Toast mínimo (usa window.toast si existe; si no, render liviano; último recurso: alert) */
export const dtoast = (message: string, ms = 2600) => {
  try {
    // Si la app principal ya provee un toast global, úsalo
    const w = window as any
    if (typeof w.toast === 'function') {
      w.toast(message)
      return
    }

    // Render propio super light
    let container = document.getElementById('__mini_toast_container__')
    if (!container) {
      container = document.createElement('div')
      container.id = '__mini_toast_container__'
      container.style.position = 'fixed'
      container.style.zIndex = '2147483647'
      container.style.right = '16px'
      container.style.bottom = '16px'
      container.style.display = 'flex'
      container.style.flexDirection = 'column'
      container.style.gap = '8px'
      document.body.appendChild(container)
    }

    const el = document.createElement('div')
    el.textContent = message
    el.style.padding = '10px 12px'
    el.style.maxWidth = '320px'
    el.style.borderRadius = '10px'
    el.style.boxShadow = '0 6px 18px rgba(0,0,0,.12)'
    el.style.background = '#1c8b56'
    el.style.color = '#fff'
    el.style.fontSize = '14px'
    el.style.lineHeight = '1.25'
    el.style.opacity = '0'
    el.style.transform = 'translateY(8px)'
    el.style.transition = 'all .18s ease'

    container.appendChild(el)
    // enter
    requestAnimationFrame(() => {
      el.style.opacity = '1'
      el.style.transform = 'translateY(0)'
    })

    // leave
    const timeout = setTimeout(() => {
      el.style.opacity = '0'
      el.style.transform = 'translateY(8px)'
      setTimeout(() => {
        el.remove()
        // Limpieza del contenedor si queda vacío
        if (container && container.childElementCount === 0) {
          container.remove()
        }
      }, 200)
    }, ms)

    // Cerrar al click
    el.addEventListener('click', () => {
      clearTimeout(timeout)
      el.style.opacity = '0'
      el.style.transform = 'translateY(8px)'
      setTimeout(() => {
        el.remove()
        if (container && container.childElementCount === 0) container.remove()
      }, 150)
    })
  } catch {
    // Último recurso
    try { alert(message) } catch {}
  }
}

// Export útil por si querés consultar desde otros módulos
export const DEBUG = DEBUG_ENABLED
